import { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Form, Spinner } from 'react-bootstrap';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { getSocket } from '../socket';
import './Display.css';
import AgendaItemModal from '../components/Meeting/modals/AgendaItemModal';
import useMeetingAgendaUpdates from '../components/Meeting/useMeetingAgendaUpdates';
import {
  clearPairedDisplayCredentials,
  getPairedDisplayCredentials,
  savePairedDisplayCredentials,
} from '../utils/pairedDisplay';

const apiUrl = process.env.REACT_APP_URL;

export default function Display() {
  const [searchParams] = useSearchParams();
  const { meetingId: routeMeetingId } = useParams();
  const navigate = useNavigate();
  const [credentials, setCredentials] = useState(getPairedDisplayCredentials);
  const [pairingCode, setPairingCode] = useState(searchParams.get('code') || '');
  const [pairing, setPairing] = useState(false);
  const [connectionState, setConnectionState] = useState('Connecting');
  const [error, setError] = useState('');
  const [command, setCommand] = useState(null);
  const [meeting, setMeeting] = useState(null);
  const socketRef = useRef(null);
  const commandRef = useRef(command);
  const meetingLoadSequence = useRef(0);

  useEffect(() => {
    if (!credentials) return undefined;
    document.body.classList.add('display-runtime-active');
    return () => document.body.classList.remove('display-runtime-active');
  }, [credentials]);

  useEffect(() => {
    commandRef.current = command;
  }, [command]);

  const pair = async (event) => {
    event?.preventDefault();
    setPairing(true);
    setError('');
    try {
      const response = await fetch(`${apiUrl}/display-devices/pair`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: pairingCode.trim() }),
      });
      const payload = await response.json();
      if (!response.ok) throw new Error(payload?.Error || 'Unable to pair this display.');
      savePairedDisplayCredentials(payload);
      setCredentials(payload);
      setPairingCode('');
    } catch (pairError) {
      setError(pairError.message || 'Unable to pair this display.');
    } finally {
      setPairing(false);
    }
  };

  const loadMeeting = useCallback(async (meetingId, agendaItemId) => {
    const sequence = ++meetingLoadSequence.current;
    if (!meetingId) {
      setMeeting(null);
      return;
    }
    try {
      const meetingResponse = await fetch(`${apiUrl}/meeting/${meetingId}`);
      if (!meetingResponse.ok) throw new Error('Could not load the meeting display.');
      const meetingData = await meetingResponse.json();
      if (sequence !== meetingLoadSequence.current) return;
      setMeeting(meetingData);
      if (agendaItemId && !meetingData.items?.some((entry) => String(entry.agendaItemId) === String(agendaItemId))) {
        setError('The selected agenda item is no longer available.');
      }
    } catch (loadError) {
      setError(loadError.message || 'Could not load the meeting display.');
    }
  }, []);

  useEffect(() => {
    if (!credentials?.deviceId || !credentials?.deviceToken) return undefined;
    const socket = getSocket();
    socketRef.current = socket;

    const register = () => {
      setConnectionState('Connecting');
      socket.emit('display:register', credentials, (result) => {
        if (!result?.ok) {
          setConnectionState('Disconnected');
          setError(result?.error || 'This display could not connect.');
          return;
        }
        setConnectionState('Connected');
        setError('');
        if (commandRef.current?.meetingId) socket.emit('meeting:join', commandRef.current.meetingId);
      });
    };
    const handleDisconnect = () => setConnectionState('Disconnected');
    const handleConnectError = (connectError) => {
      setConnectionState('Disconnected');
      setError(connectError.message || 'Connection failed.');
    };
    const handleCommand = (nextCommand) => {
      commandRef.current = nextCommand;
      setCommand(nextCommand);
      if (nextCommand?.meetingId) {
        navigate(`/display/${nextCommand.meetingId}`, { replace: true });
        socket.emit('meeting:join', String(nextCommand.meetingId));
        void loadMeeting(nextCommand.meetingId, nextCommand.agendaItemId);
      } else {
        navigate('/display', { replace: true });
        setMeeting(null);
      }
    };
    const heartbeat = window.setInterval(() => {
      if (socket.connected) socket.emit('display:heartbeat');
      else socket.connect();
    }, 25000);

    socket.on('connect', register);
    socket.on('disconnect', handleDisconnect);
    socket.on('connect_error', handleConnectError);
    socket.on('display:command', handleCommand);
    if (socket.connected) register();

    return () => {
      window.clearInterval(heartbeat);
      socket.off('connect', register);
      socket.off('disconnect', handleDisconnect);
      socket.off('connect_error', handleConnectError);
      socket.off('display:command', handleCommand);
      if (socket.connected) socket.emit('display:unregister');
      socketRef.current = null;
    };
  }, [credentials, loadMeeting, navigate]);

  const selectedItem = meeting?.items?.find(
    (item) => String(item.agendaItemId) === String(command?.agendaItemId)
  );
  const refreshMeeting = useCallback(() => {
    if (!commandRef.current?.meetingId) return Promise.resolve();
    return loadMeeting(commandRef.current.meetingId, commandRef.current.agendaItemId);
  }, [loadMeeting]);
  useMeetingAgendaUpdates(command?.meetingId, refreshMeeting);
  const closeRemoteDisplay = useCallback(() => {
    if (socketRef.current?.connected) socketRef.current.emit('display:close');
    commandRef.current = null;
    setCommand(null);
    navigate('/display', { replace: true });
  }, [navigate]);

  if (!credentials) {
    return (
      <main className="display-pair-page">
        <Form className="display-pair-card" onSubmit={pair}>
          <h1>Pair this display</h1>
          <p>No account or sign-in is required. Ask the meeting’s Timer Manager to register this display, then scan its QR code or enter the pairing code below.</p>
          {error && <p role="alert" className="display-error">{error}</p>}
          <Form.Control
            autoFocus
            inputMode="numeric"
            maxLength={10}
            value={pairingCode}
            onChange={(event) => setPairingCode(event.target.value.replace(/\D/g, '').slice(0, 10))}
            aria-label="10-digit pairing code"
            placeholder="0000000000"
          />
          <Button type="submit" disabled={pairing || pairingCode.length !== 10}>
            {pairing ? <Spinner size="sm" animation="border" /> : 'Pair display'}
          </Button>
        </Form>
      </main>
    );
  }

  return (
    <main className="display-runtime">
      <div className="display-runtime-status">{connectionState}</div>
      {error && <div role="alert" className="display-error">{error}</div>}
      {command?.meetingId && String(routeMeetingId) === String(command.meetingId) && selectedItem ? (
        <AgendaItemModal
          key={`${command.meetingId}:${command.agendaItemId}`}
          agenda={meeting.items}
          item={selectedItem}
          getMeeting={refreshMeeting}
          meeting={meeting}
          showModal
          handleCloseModal={closeRemoteDisplay}
          showClockText={command.showTimer !== false}
          timerDisplayEnabled={command.showTimer !== false}
          remoteControlled
        />
      ) : (
        <section className="display-runtime-waiting">
          <h1>{credentials.name || 'Display ready'}</h1>
          <p>{connectionState === 'Connected' ? 'Awaiting instructions from the meeting controller' : 'Reconnecting to the meeting server…'}</p>
        </section>
      )}
      <Button
        className="display-unpair"
        variant="outline-light"
        size="sm"
        hidden={Boolean(command?.meetingId)}
        onClick={() => {
          clearPairedDisplayCredentials();
          if (socketRef.current?.connected) socketRef.current.emit('display:unregister');
          setCredentials(null);
          commandRef.current = null;
          setCommand(null);
          navigate('/display', { replace: true });
        }}
      >
        Unpair
      </Button>
    </main>
  );
}
